import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  // type ICurrencyPrice,
  type ICurrencyType,
  type IEbayMarketplace,
  type IProductEbayDetail,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Input, Row, Select, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { type Dispatch } from 'redux';

import { EMPTY_VALUE } from '../../../fragments/empty-value';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { ebayProductSave } from '../../../reducers/ebay/reducer';
import { SPACING } from '../../../theme/tokens';
import Link from '../../link';

interface ISelectOption {
  value: string;
  label: string;
}

interface ISelectOption {
  value: string;
  label: string;
}

const initialValues = {
  inStock: 2,
  priceIncreaseRate: 15,
  paymentPolicyId: '',
  fulfillmentPolicyId: '',
  returnPolicyId: '',
};

const onFinishMarketplace = (
  { name },
  {
    paymentPolicyId,
    fulfillmentPolicyId,
    returnPolicyId,
    inStock,
    priceIncreaseRate,
  },
  productUUID: string,
  dispatch: Dispatch<ReturnType<typeof ebayProductSave>>,
): void => {
  dispatch(
    ebayProductSave(
      productUUID,
      name,
      paymentPolicyId,
      fulfillmentPolicyId,
      returnPolicyId,
      inStock,
      priceIncreaseRate,
    ),
  );
};

function EBayMarketplaceContainer({
  Marketplace,
}: {
  Marketplace: IEbayMarketplace;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const Currency = useSelector((s: RootState) => s.Currency);
  const Ebay = useSelector((s: RootState) => s.Ebay);
  const Product = useSelector((s: RootState) => s.Product);
  const [form] = Form.useForm();
  const priceIncreaseRateValue = Form.useWatch('priceIncreaseRate', form);

  const [paymentSelectOptions, setPaymentSelectOptions] = useState<
    ISelectOption[]
  >([]);
  const [fulfillmentSelectOptions, setFulfillmentSelectOptions] = useState<
    ISelectOption[]
  >([]);
  const [returnSelectOptions, setReturnSelectOptions] = useState<
    ISelectOption[]
  >([]);
  const [productEbay, setProductEbay] = useState<
    IProductEbayDetail | undefined | null
  >(undefined);
  const [productPriceEUR, setProductPriceEUR] = useState<number | undefined>(
    undefined,
  );
  const [productPriceUSD, setProductPriceUSD] = useState<number | undefined>(
    undefined,
  );

  const [currencyTypeEUR, setCurrencyTypeEUR] = useState<
    ICurrencyType | undefined
  >(undefined);
  const [currencyTypeUSD, setCurrencyTypeUSD] = useState<
    ICurrencyType | undefined
  >(undefined);

  useEffect(() => {
    const paymentSelectOptionsUE = Ebay.paymentPolicies[Marketplace.code]
      ?.sort((a, b) => stringCompare(a.name, b.name))
      .map(({ name, paymentPolicyId }) => {
        const pso: ISelectOption = {
          value: paymentPolicyId,
          label: name,
        };

        return pso;
      });

    setPaymentSelectOptions(paymentSelectOptionsUE);
  }, [Ebay.paymentPolicies, Marketplace.code]);

  useEffect(() => {
    const fulfillmentSelectOptionsUE = Ebay.fulfillmentPolicies[
      Marketplace.code
    ]
      ?.sort((a, b) => stringCompare(a.name, b.name))
      .map(({ name, fulfillmentPolicyId }) => {
        const fso: ISelectOption = {
          value: fulfillmentPolicyId,
          label: name,
        };

        return fso;
      });

    setFulfillmentSelectOptions(fulfillmentSelectOptionsUE);
  }, [Ebay.fulfillmentPolicies, Marketplace.code]);

  useEffect(() => {
    const returnSelectOptionsUE = Ebay.returnPolicies[Marketplace.code]
      ?.sort((a, b) => stringCompare(a.name, b.name))
      .map(({ name, returnPolicyId }) => {
        const rso: ISelectOption = {
          value: returnPolicyId,
          label: name,
        };

        return rso;
      });

    setReturnSelectOptions(returnSelectOptionsUE);
  }, [Ebay.returnPolicies, Marketplace.code]);

  useEffect(() => {
    const productEbayUE = Ebay.productEbay[Marketplace.code];

    setProductEbay(productEbayUE);
  }, [Ebay.productEbay, Marketplace.code]);

  useEffect(() => {
    if (productEbay) {
      form.setFieldsValue({
        inStock: productEbay.inStock,
        priceIncreaseRate: productEbay.priceIncreaseRate,
        paymentPolicyId: productEbay.paymentPolicy.id,
        fulfillmentPolicyId: productEbay.fulfillmentPolicy.id,
        returnPolicyId: productEbay.returnPolicy.id,
      });
    }
  }, [productEbay, form]);

  useEffect(() => {
    // const productPriceEURUE: number | undefined = Product.prices.find(
    //   ({ isoCode }: ICurrencyPrice): boolean => isoCode === 'EUR',
    // )?.price;

    // const productPriceUSDUE: number | undefined = Product.prices.find(
    //   ({ isoCode }: ICurrencyPrice): boolean => isoCode === 'USD',
    // )?.price;

    // setProductPriceEUR(productPriceEURUE);
    setProductPriceEUR(0);
    // setProductPriceUSD(productPriceUSDUE);
    setProductPriceUSD(0);
  }, [Product.prices, Product.uuid]);

  useEffect(() => {
    const currencyTypeEURUE = Currency.currencies.find(
      ({ isoCode }) => isoCode === 'EUR',
    );
    const currencyTypeUSDUE = Currency.currencies.find(
      ({ isoCode }) => isoCode === 'USD',
    );

    setCurrencyTypeEUR(currencyTypeEURUE);
    setCurrencyTypeUSD(currencyTypeUSDUE);
  }, [Currency.currencies]);

  return (
    <Form
      onFinish={(values) =>
        onFinishMarketplace(Marketplace, values, Product.uuid, dispatch)
      }
      disabled={Ebay.inProcess}
      initialValues={initialValues}
      form={form}
    >
      <Divider titlePlacement="start">
        <Space size={SPACING.lg} align="start">
          {Marketplace.name}
          <Link to={`/ebay/marketplace/${Marketplace.code}`} openInNewTab />
        </Space>
      </Divider>
      <Row gutter={SPACING.lg}>
        <Col span={4}>
          <Form.Item
            name="paymentPolicyId"
            rules={[
              {
                required: true,
              },
            ]}
            label="Payment policy"
          >
            <Select
              placeholder="Payment policy"
              options={paymentSelectOptions}
            />
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item
            name="fulfillmentPolicyId"
            rules={[
              {
                required: true,
              },
            ]}
            label="Fulfillment policy"
          >
            <Select
              placeholder="Fulfillment policy"
              options={fulfillmentSelectOptions}
            />
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item
            name="returnPolicyId"
            rules={[
              {
                required: true,
              },
            ]}
            label="Return policy"
          >
            <Select placeholder="Return policy" options={returnSelectOptions} />
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item>
            <Space.Compact>
              <Button type="default" style={{ pointerEvents: 'none' }}>
                In stock
              </Button>
              <Form.Item
                name="inStock"
                noStyle
                rules={[
                  {
                    required: true,
                    type: 'number',
                    min: 0,
                    message: 'In stock must be a positive number.',
                    transform: (value) => Number(value),
                  },
                ]}
              >
                <Input type="number" />
              </Form.Item>
              <Button type="default" style={{ pointerEvents: 'none' }}>
                pcs
              </Button>
            </Space.Compact>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item>
            <Space.Compact>
              <Button type="default" style={{ pointerEvents: 'none' }}>
                Price increase rate
              </Button>
              <Form.Item
                name="priceIncreaseRate"
                noStyle
                rules={[
                  {
                    required: true,
                    type: 'number',
                    min: 0,
                    message: 'Price increase rate must be a positive number.',
                    transform: (value) => Number(value),
                  },
                ]}
              >
                <Input type="number" />
              </Form.Item>
              <Button type="default" style={{ pointerEvents: 'none' }}>
                %
              </Button>
            </Space.Compact>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Col>

        <Col span={4}>
          <Space orientation="vertical" size={SPACING.lg} align="start">
            <strong>
              Ebay price in EUR will be{' '}
              {productPriceEUR && currencyTypeEUR
                ? formatCurrency(
                    productPriceEUR * (1 + priceIncreaseRateValue / 100),
                    currencyTypeEUR,
                  )
                : EMPTY_VALUE}
              .
            </strong>
            <strong>
              Ebay price in USD will be{' '}
              {productPriceUSD && currencyTypeUSD
                ? formatCurrency(
                    productPriceUSD * (1 + priceIncreaseRateValue / 100),
                    currencyTypeUSD,
                  )
                : EMPTY_VALUE}
              .
            </strong>
          </Space>
        </Col>
      </Row>
    </Form>
  );
}

export default EBayMarketplaceContainer;
